import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Users, TrendingUp, Award, BookOpen, Search, ChevronRight, X, MessageSquare, Send, Calendar, Star, Flame, Zap, AlertTriangle, Clock, ChevronDown, ChevronUp, Download, Sparkles, ArrowRight } from '../components/CustomIcons';
import StreakCalendar from '../components/StreakCalendar';
import EmptyState from '../components/EmptyState';
import WelcomeModal from '../components/WelcomeModal';
import { useMarkOnboardingPath } from '../components/WelcomeModal';

function ProgressBar({ value, color = 'var(--orange)' }) {
  return (
    <div style={{ height: '6px', background: 'var(--bg-elevated)', borderRadius: '3px', overflow: 'hidden', width: '100%' }}>
      <div style={{ height: '100%', background: color, borderRadius: '3px', width: `${Math.min(value, 100)}%`, transition: 'width 0.6s ease' }} />
    </div>
  );
}

function LearnerModal({ learner, onClose, authFetch }) {
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('spotlight');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    authFetch(`/admin/caseload/${learner.id}`)
      .then(r => setDetail(r.data))
      .catch(() => {});
  }, [learner.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      const res = await authFetch(`/admin/notes/${learner.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: { content: note.trim() },
      });
      const raw = res.data;
      const newNote = {
        id: raw.id || String(Date.now()),
        staff_name: raw.staff_name || raw.author_name || 'Staff',
        note: raw.note || raw.content || note.trim(),
        created_at: raw.created_at || new Date().toISOString(),
      };
      setDetail(prev => prev ? { ...prev, notes: [newNote, ...(prev.notes || [])] } : prev);
      setNote('');
    } catch (e) { console.error('add-note error:', e); }
    finally { setSaving(false); }
  };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await authFetch(`/admin/reports/learner/${learner.id}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AssistivOS_Report_${learner.first_name}_${learner.last_name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { console.error('download-report error:', e); }
    finally { setDownloading(false); }
  };

  const profile = detail?.learner?.profile || {};
  const weekly = detail?.weekly_summary || {};
  const loginDates = detail?.login_dates || [];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '24px' }}
      onClick={onClose}
      aria-label="Learner spotlight backdrop"
      role="presentation">
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '18px', padding: '28px', maxWidth: '680px', width: '100%', maxHeight: '88vh', overflow: 'auto' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="spotlight-title"
        onClick={e => e.stopPropagation()} data-testid="learner-detail-modal">

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '50px', height: '50px', borderRadius: '50%', background: 'linear-gradient(135deg, #F97316, #F0943A)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '18px', color: '#111111' }}>
              {learner.first_name[0]}{learner.last_name[0]}
            </div>
            <div>
              <h2 id="spotlight-title" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '20px', color: 'var(--text-primary)', margin: '0 0 3px 0' }}>
                {learner.first_name} {learner.last_name}
              </h2>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>{learner.email}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px' }}>
            <X size={20} />
          </button>
        </div>

        {/* Download Report button */}
        <div style={{ marginBottom: '16px' }}>
          <button
            onClick={downloadReport}
            disabled={downloading}
            data-testid="download-report-btn"
            aria-label="Download learner progress report as PDF"
            style={{
              display: 'flex', alignItems: 'center', gap: '7px',
              padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--border-default)',
              background: downloading ? 'var(--bg-elevated)' : 'var(--orange)',
              color: downloading ? 'var(--text-muted)' : '#111111',
              fontSize: '13px', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif',
              cursor: downloading ? 'not-allowed' : 'pointer', transition: 'all 0.15s',
            }}>
            <Download size={14} aria-hidden="true" />
            {downloading ? 'Generating PDF…' : 'Download Report'}
          </button>
        </div>

        {/* Quick stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '20px' }}>
          {[
            { label: 'Credits', value: ((learner.profile?.credits || learner.credits || 0)).toLocaleString(), icon: Zap, color: '#F97316' },
            { label: 'Streak', value: `${learner.profile?.streak_days || learner.streak_days || 0}d`, icon: Flame, color: '#F59E0B' },
            { label: 'Credentials', value: detail?.credentials?.length ?? learner.credential_count ?? 0, icon: Award, color: '#22C55E' },
            { label: 'This Week', value: detail ? (detail.weekly_summary?.lessons_this_week ?? 0) : '…', icon: BookOpen, color: '#3B82F6' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} style={{ background: 'var(--bg-elevated)', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
              <Icon size={14} style={{ color, marginBottom: '4px' }} />
              <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '18px', color: 'var(--text-primary)', marginBottom: '2px' }}>{value}</div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-elevated)', borderRadius: '10px', padding: '4px', marginBottom: '20px' }}>
          {[
            { id: 'spotlight', label: 'Spotlight' },
            { id: 'credentials', label: `Credentials (${detail?.credentials?.length || 0})` },
            { id: 'recaps', label: `Recaps (${(detail?.counselor_recaps || []).length})` },
            { id: 'notes', label: `Notes (${(detail?.notes || []).length})` },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ flex: 1, padding: '7px', borderRadius: '7px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif', background: tab === t.id ? 'var(--bg-surface)' : 'transparent', color: tab === t.id ? 'var(--text-primary)' : 'var(--text-muted)', transition: 'all 0.15s' }}
              data-testid={`spotlight-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* SPOTLIGHT TAB */}
        {tab === 'spotlight' && (
          <div>
            {/* Current pathway */}
            {learner.pathway_name && (
              <div style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600 }}>
                    {learner.pathway_name}
                  </span>
                  <span style={{ fontSize: '13px', color: 'var(--orange)', fontWeight: 700 }}>{learner.pathway_progress}%</span>
                </div>
                <ProgressBar value={learner.pathway_progress} />
              </div>
            )}

            {/* Weekly summary tiles */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' }}>
              {[
                { label: 'Lessons this week', value: weekly.lessons_this_week ?? '—', color: '#3B82F6' },
                { label: 'Credits this week', value: weekly.credits_this_week ?? '—', color: '#F97316' },
                { label: 'AI Counselor sessions', value: detail?.ai_tutor_sessions ?? '—', color: '#8B5CF6' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: 'var(--bg-elevated)', borderRadius: '10px', padding: '14px', textAlign: 'center' }} data-testid={`spotlight-stat-${label.split(' ')[0].toLowerCase()}`}>
                  <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '22px', color, marginBottom: '4px' }}>{value}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.3 }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Streak calendar */}
            <StreakCalendar loginDates={loginDates} streakDays={profile.streak_days || learner.streak_days || 0} />
          </div>
        )}

        {/* CREDENTIALS TAB */}
        {tab === 'credentials' && (
          <div>
            {detail?.credentials?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {detail.credentials.map(c => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: 'var(--bg-elevated)', borderRadius: '10px' }}>
                    <Award size={14} style={{ color: 'var(--orange)', flexShrink: 0 }} />
                    <span style={{ fontSize: '13px', color: 'var(--text-primary)', flex: 1 }}>{c.title}</span>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{new Date(c.date_earned || c.issued_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '24px', fontSize: '14px' }}>No credentials earned yet — in training.</p>
            )}
          </div>
        )}

        {/* RECAPS TAB */}
        {tab === 'recaps' && (
          <div data-testid="spotlight-recaps-tab">
            {(detail?.counselor_recaps || []).length > 0 ? (
              (detail.counselor_recaps).map((r, i) => (
                <div key={`${r.session_id}-${i}`} style={{ padding: '14px 16px', background: r.flagged ? 'rgba(239,68,68,0.07)' : 'var(--bg-elevated)', borderRadius: '10px', marginBottom: '8px', border: r.flagged ? '1px solid rgba(239,68,68,0.25)' : '1px solid transparent' }} data-testid={`recap-item-${i}`}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '6px' }}>
                    <Sparkles size={13} color="#F97316" style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'Plus Jakarta Sans, sans-serif', flex: 1 }}>{r.title}</span>
                    {r.flagged && (
                      <span data-testid={`recap-flag-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(239,68,68,0.12)', color: '#EF4444', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', padding: '2px 7px', borderRadius: '99px', flexShrink: 0 }}>
                        <AlertTriangle size={10} />NEEDS ATTENTION
                      </span>
                    )}
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  <p style={{ fontSize: '12.5px', color: 'var(--text-secondary)', margin: '0 0 8px', lineHeight: 1.55 }}>{r.summary}</p>
                  {r.next_step && (
                    <div style={{ display: 'flex', gap: '7px', alignItems: 'flex-start' }}>
                      <ArrowRight size={12} color="#F97316" style={{ flexShrink: 0, marginTop: '3px' }} />
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}><b style={{ color: 'var(--text-primary)' }}>Suggested next step:</b> {r.next_step}</span>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', textAlign: 'center', padding: '24px' }}>No counselor session recaps yet — recaps appear when this learner wraps up an AI Counselor conversation.</p>
            )}
          </div>
        )}

        {/* NOTES TAB */}
        {tab === 'notes' && (
          <div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <input className="input-dark" value={note} onChange={e => setNote(e.target.value)}
                placeholder="Add a case note..." style={{ flex: 1 }}
                onKeyDown={e => e.key === 'Enter' && addNote()}
                data-testid="note-input" />
              <button className="btn-primary" onClick={addNote} disabled={saving || !note.trim()} style={{ flexShrink: 0 }}
                data-testid="add-note-btn">
                <Send size={14} />
              </button>
            </div>
            {(detail?.notes || learner.recent_notes || []).map(n => (
              <div key={n.id} style={{ padding: '10px 14px', background: 'var(--bg-elevated)', borderRadius: '8px', marginBottom: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--orange)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{n.staff_name}</span>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{new Date(n.created_at).toLocaleDateString()}</span>
                </div>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>{n.note}</p>
              </div>
            ))}
            {(detail?.notes || learner.recent_notes || []).length === 0 && (
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', textAlign: 'center', padding: '16px' }}>No notes yet. Add the first note above.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CaseManager() {
  const { authFetch } = useAuth();
  const [learners, setLearners] = useState([]);
  const [atRisk, setAtRisk] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [alertsExpanded, setAlertsExpanded] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchDownloading, setBatchDownloading] = useState(false);

  useEffect(() => {
    Promise.all([
      authFetch('/admin/caseload'),
      authFetch('/admin/at-risk'),
    ])
      .then(([caseR, riskR]) => {
        setLearners(caseR.data || []);
        setAtRisk(riskR.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = learners.filter(l => {
    const name = `${l.first_name} ${l.last_name} ${l.email}`.toLowerCase();
    const matchSearch = !search || name.includes(search.toLowerCase());
    const matchFilter = filter === 'all'
      || (filter === 'active' && (l.streak_days || 0) > 0)
      || (filter === 'at_risk' && (l.pathway_progress || 0) < 15 && (l.lessons_completed || 0) < 5)
      || (filter === 'near_release' && l.release_date);
    return matchSearch && matchFilter;
  });

  useMarkOnboardingPath('/case-manager');

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(l => l.id)));
    }
  };

  const downloadBatchReport = async () => {
    if (!selectedIds.size) return;
    setBatchDownloading(true);
    try {
      const res = await authFetch('/admin/reports/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: { learner_ids: [...selectedIds] },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AssistivOS_Batch_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) { console.error('batch-report error:', err); }
    finally { setBatchDownloading(false); }
  };

  const totalLearners = learners.length;
  const activeToday = learners.filter(l => (l.streak_days || 0) > 0).length;
  const totalCreds = learners.reduce((a, l) => a + (l.credentials_earned || 0), 0);
  const avgProgress = learners.length > 0 ? Math.round(learners.reduce((a, l) => a + (l.pathway_progress || 0), 0) / learners.length) : 0;

  return (
    <div className="page-wrapper" style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="case-manager-page">
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '28px' }}>
          <div className="overline" style={{ marginBottom: '6px' }}>Case Management</div>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '28px', color: 'var(--text-primary)', margin: '0 0 6px 0' }}>Caseload Dashboard</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: 0 }}>Track learner progress, credentials, and reentry milestones across your full caseload.</p>
        </div>

        {/* Overview Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px', marginBottom: '24px' }}>
          {[
            { icon: Users, label: 'Total Learners', value: totalLearners, color: 'var(--orange)' },
            { icon: TrendingUp, label: 'Active Today', value: activeToday, color: '#3B82F6' },
            { icon: Award, label: 'Credentials Earned', value: totalCreds, color: '#22C55E' },
            { icon: BookOpen, label: 'Avg Progress', value: `${avgProgress}%`, color: '#8B5CF6' },
          ].map(({ icon: Icon, label, value, color }) => (
            <div key={label} className="card" style={{ padding: '18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={16} style={{ color }} />
                </div>
              </div>
              <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--text-primary)', marginBottom: '2px' }}>{value}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{label}</div>
            </div>
          ))}
        </div>

        {/* ── At-Risk Alerts ─────────────────────────────────────────── */}
        {atRisk.length > 0 && (
          <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '14px', marginBottom: '24px', overflow: 'hidden' }}
            data-testid="at-risk-panel">
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', cursor: 'pointer' }}
              onClick={() => setAlertsExpanded(p => !p)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <AlertTriangle size={16} style={{ color: '#EF4444' }} />
                <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: '#EF4444' }}>
                  At-Risk Learners
                </span>
                <span style={{ background: '#EF4444', color: '#fff', borderRadius: '20px', fontSize: '11px', fontWeight: 800, padding: '2px 8px' }}
                  data-testid="at-risk-badge">
                  {atRisk.length}
                </span>
              </div>
              {alertsExpanded ? <ChevronUp size={16} style={{ color: '#EF4444' }} /> : <ChevronDown size={16} style={{ color: '#EF4444' }} />}
            </div>

            {alertsExpanded && (
              <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {atRisk.slice(0, 8).map(a => (
                  <div key={a.id}
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'var(--bg-elevated)', borderRadius: '10px', padding: '10px 14px', cursor: 'pointer' }}
                    onClick={() => { const l = learners.find(x => x.id === a.id); if (l) setSelected(l); }}
                    data-testid={`at-risk-row-${a.id}`}>
                    <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'rgba(239,68,68,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <AlertTriangle size={14} style={{ color: '#EF4444' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)', margin: '0 0 3px 0' }}>
                        {a.first_name} {a.last_name}
                      </p>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {a.risk_reasons.map((r) => (
                          <span key={r} style={{ fontSize: '10px', background: 'rgba(239,68,68,0.12)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '4px', padding: '1px 6px', fontWeight: 600 }}>
                            {r}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                      <Clock size={11} style={{ color: 'var(--text-muted)' }} />
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{a.days_since_login ?? '?'}d ago</span>
                    </div>
                  </div>
                ))}
                {atRisk.length > 8 && (
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '4px' }}>
                    +{atRisk.length - 8} more at-risk learners
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Search + Filter + Batch Export */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input className="input-dark" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              aria-label="Search learners by name or email"
              style={{ paddingLeft: '38px' }}
              data-testid="caseload-search" />
          </div>
          <div role="group" aria-label="Filter learners" style={{ display: 'flex', gap: '4px', background: 'var(--bg-elevated)', padding: '4px', borderRadius: '10px' }}>
            {[
              { value: 'all', label: 'All' },
              { value: 'active', label: 'Active' },
              { value: 'at_risk', label: 'At Risk' },
              { value: 'near_release', label: 'Near Release' },
            ].map(f => (
              <button key={f.value} onClick={() => setFilter(f.value)}
                aria-pressed={filter === f.value}
                data-testid={`filter-${f.value}`}
                style={{ padding: '6px 14px', borderRadius: '7px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif', background: filter === f.value ? 'var(--bg-surface)' : 'transparent', color: filter === f.value ? 'var(--text-primary)' : 'var(--text-muted)', transition: 'all 0.15s ease' }}>
                {f.label}
              </button>
            ))}
          </div>

          {/* Batch export controls */}
          {filtered.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={toggleSelectAll}
                data-testid="select-all-btn"
                aria-label={selectedIds.size === filtered.length ? 'Deselect all learners' : 'Select all learners'}
                style={{ padding: '7px 14px', borderRadius: '8px', border: '1px solid var(--border-default)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif', cursor: 'pointer' }}>
                {selectedIds.size === filtered.length ? 'Deselect All' : 'Select All'}
              </button>
              {selectedIds.size > 0 && (
                <button
                  onClick={downloadBatchReport}
                  disabled={batchDownloading}
                  data-testid="batch-export-btn"
                  aria-label={`Export ${selectedIds.size} selected learner reports as PDF`}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 16px', borderRadius: '8px', border: 'none', background: batchDownloading ? 'var(--bg-elevated)' : 'var(--orange)', color: batchDownloading ? 'var(--text-muted)' : '#111111', fontSize: '12px', fontWeight: 700, fontFamily: 'Plus Jakarta Sans, sans-serif', cursor: batchDownloading ? 'not-allowed' : 'pointer', transition: 'all 0.15s' }}>
                  <Download size={13} aria-hidden="true" />
                  {batchDownloading ? 'Generating…' : `Export Selected (${selectedIds.size})`}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Learner Grid */}
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
            {[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: '180px', borderRadius: '14px' }} />)}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Users}
            title={search || filter !== 'all' ? 'No learners match your search or filter' : 'No learners in your caseload yet'}
            subtitle={search || filter !== 'all' ? 'Try a different name or adjust the filter.' : 'Learners assigned to your caseload will appear here.'}
            action={search ? { label: 'Clear search', onClick: () => setSearch('') } : undefined}
            testId="caseload-empty"
          />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
            {filtered.map(learner => (
              <div key={learner.id} className="card"
                style={{ padding: '18px', cursor: 'pointer', transition: 'all 0.2s ease', position: 'relative', outline: selectedIds.has(learner.id) ? '2px solid var(--orange)' : 'none' }}
                onClick={() => setSelected(learner)}
                data-testid={`learner-card-${learner.id}`}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = 'var(--border-hover)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = ''; }}>

                {/* Selection checkbox */}
                <div
                  onClick={e => toggleSelect(learner.id, e)}
                  data-testid={`select-learner-${learner.id}`}
                  aria-label={`Select ${learner.first_name} ${learner.last_name}`}
                  role="checkbox"
                  aria-checked={selectedIds.has(learner.id)}
                  style={{ position: 'absolute', top: '12px', right: '12px', width: '18px', height: '18px', borderRadius: '4px', border: `2px solid ${selectedIds.has(learner.id) ? 'var(--orange)' : 'var(--border-default)'}`, background: selectedIds.has(learner.id) ? 'var(--orange)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 2, transition: 'all 0.15s' }}>
                  {selectedIds.has(learner.id) && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4L3.5 6.5L9 1" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'linear-gradient(135deg, #F97316, #F0943A)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '16px', color: '#111111', flexShrink: 0 }}>
                    {learner.first_name[0]}{learner.last_name[0]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)', margin: '0 0 2px 0' }}>
                      {learner.first_name} {learner.last_name}
                    </p>
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {learner.email}
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                    {(learner.streak_days || 0) > 0 && (
                      <span style={{ fontSize: '11px', background: 'rgba(249,115,22,0.12)', color: 'var(--orange)', border: '1px solid rgba(249,115,22,0.25)', borderRadius: '4px', padding: '2px 6px', fontWeight: 700 }}>
                        {learner.streak_days}d
                      </span>
                    )}
                    <ChevronRight size={15} style={{ color: 'var(--text-muted)' }} />
                  </div>
                </div>

                {/* Pathway Progress */}
                {learner.pathway_name && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{learner.pathway_name}</span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--orange)' }}>{learner.pathway_progress}%</span>
                    </div>
                    <ProgressBar value={learner.pathway_progress} />
                  </div>
                )}

                {/* Stats row — show zero-progress EmptyState when no activity yet */}
                {(learner.lessons_completed === 0 && learner.credentials_earned === 0) ? (
                  <div onClick={e => e.stopPropagation()}>
                    <EmptyState
                      compact
                      icon={BookOpen}
                      title="No lessons started yet"
                      subtitle="This learner hasn't begun their pathway."
                      action={{ label: 'View learner plan', onClick: (e) => { e.stopPropagation(); setSelected(learner); } }}
                      testId={`learner-zero-${learner.id}`}
                    />
                  </div>
                ) : (
                <div style={{ display: 'flex', gap: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Award size={12} style={{ color: 'var(--text-muted)' }} />
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{learner.credentials_earned} creds</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <BookOpen size={12} style={{ color: 'var(--text-muted)' }} />
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{learner.lessons_completed} lessons</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Star size={12} style={{ color: 'var(--text-muted)' }} />
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Lvl {learner.level}</span>
                  </div>
                </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {selected && <LearnerModal learner={selected} onClose={() => setSelected(null)} authFetch={authFetch} />}
      <WelcomeModal />
    </div>
  );
}
